import { Delete } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Link,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useCallback } from 'react';
import {
  Control,
  Controller,
  FieldArrayWithId,
  UseFieldArrayRemove,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useConfirmDialog } from '~/components/DialogsProvider';
import { QuickLinksFormValues } from '~/scenes/MyWorkspace/components/Widgets/components/QuickLinksWidget/models';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';

import { useSxStyles } from './useStyles';

type Props = {
  fieldItem: FieldArrayWithId<QuickLinksFormValues, 'quickLinks'>;
  control: Control<QuickLinksFormValues>;
  index: number;
  remove: UseFieldArrayRemove;
  workspaceType?: WorkspaceType;
};

export const QuickLinkEditFields = (props: Props) => {
  const { fieldItem, control, index, remove, workspaceType } = props;
  const { location, url, name } = fieldItem;
  const isCompanyWorkspace = workspaceType === WorkspaceType.Company;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const handleRemove = useCallback(
    (index: number) => {
      confirm(t('customLinkDeleteConfirmationMessage'), (confirmed) => {
        if (confirmed) {
          remove(index);
        }
      });
    },
    [confirm, remove, t],
  );

  const canEdit = !location && !isCompanyWorkspace;

  return (
    <Box sx={sxStyles.root}>
      <Controller
        control={control}
        name={`quickLinks.${index}.active`}
        render={({ field: { value, onChange } }) => {
          return !isCompanyWorkspace ? (
            <Switch
              color="primary"
              checked={!!value}
              onChange={() => onChange(!value)}
              data-cy="toggle"
              disabled={!canEdit}
            />
          ) : (
            <></>
          );
        }}
      />
      {canEdit ? (
        <Box>
          <Controller
            control={control}
            name={`quickLinks.${index}.name`}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                data-cy="name"
                placeholder={t('linkName')}
                error={!!error}
                helperText={error?.message}
                size={'small'}
                fullWidth={true}
              />
            )}
          />
          <Controller
            control={control}
            name={`quickLinks.${index}.url`}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                data-cy="url"
                placeholder={t('url')}
                error={!!error}
                helperText={error?.message}
                size={'small'}
                fullWidth={true}
              />
            )}
          />
        </Box>
      ) : (
        <Box>
          <Typography>{name}</Typography>
          <Typography variant={'caption'}>
            <Link href={url} target="_blank">
              {url}
            </Link>
          </Typography>
        </Box>
      )}

      {canEdit && (
        <IconButton
          onClick={() => {
            handleRemove(index);
          }}
          color="primary"
          sx={sxStyles.iconButton}
          data-cy="delete"
          size="large"
        >
          <Delete />
        </IconButton>
      )}
    </Box>
  );
};
